import { time } from '@/time';
import type { Recipe } from '@/types';

const recipe: Recipe = {
  title: 'Al Pastor',
  source: 'https://www.seriouseats.com/tacos-al-pastor-recipe',
  totalTime: time(20, 50),
  servings: 6,
  wholeOnly: true,
  vessels: {
    main: { kind: 'skillet', diameter: 12 },
    pot: { kind: 'pot', quarts: 2 },
  },
  tags: { type: 'main' },
  ingredients: [
    { name: 'ancho chiles', quantity: '2',
      note: 'seeds & stems removed',
    },
    { name: 'guajillo chiles', quantity: '2',
      note: 'seeds & stems removed',
    },
    { name: 'chicken stock', quantity: '1/2', unit: 'cup',
      note: 'low-sodium',
    },
    { name: 'vegetable oil', quantity: '2', unit: 'tbsp' },
    { name: 'dried Mexican oregano', quantity: '1', unit: 'tsp' },
    { name: 'ground cumin', quantity: '1', unit: 'tsp' },
    { name: 'achiote powder', quantity: '1', unit: 'tbsp' },
    { name: 'chipotle chile', quantity: '1',
      note: 'canned in adobo sauce',
    },
    { name: 'adobo sauce', quantity: '2', unit: 'tsp',
      note: 'from canned chipotle chile',
    },
    { name: 'distilled white vinegar', quantity: '1/4', unit: 'cup' },
    { name: 'kosher salt', quantity: '2 1/2', unit: 'tsp' },
    { name: 'sugar', quantity: '2', unit: 'tsp' },
    { name: 'garlic', quantity: '3', unit: 'cloves', customUnit: true },
    { name: 'pork butt', quantity: '3', unit: 'lb',
      note: 'boneless',
    },
    { name: 'pineapple', quantity: '1',
      note: 'peeled, cored & quartered lengthwise',
    },
    { name: 'corn tortillas', quantity: '24',
      note: 'heated & kept warm',
    },
    { name: 'white onion', quantity: '1',
      note: 'finely diced',
    },
    { name: 'fresh cilantro', quantity: '1/2', unit: 'cup',
      note: 'finely minced leaves & tender stems',
    },
    { name: 'salsa verde', quantity: '1', unit: 'cup' },
    { name: 'limes', quantity: '2',
      note: 'cut into wedges',
    },
  ],
  directions: [
    {
      title: 'Toast chiles',
      bullets: [
        "In large saucepan over medium-high heat, cook {ancho chiles} & {guajillo chiles}, turning occasionally, until pliable, lightly browned & aromatic, ~5 min",
        "Add {chicken stock}",
        "Pour contents into small bowl & set aside",
        "Wipe out saucepan",
      ],
    },
    {
      title: 'Bloom spices',
      bullets: [
        "In large saucepan over medium-high heat, heat {vegetable oil:2 tsp} until shimmering",
        "Add {ground cumin}, {dried Mexican oregano} & {achiote powder}",
        "Cook, stirring frequently, until aromatic but not browned, ~30 sec",
        "Add {chipotle chile} & {adobo sauce}, cook until aromatic, ~30 sec longer",
        "Add {distilled white vinegar}, {kosher salt} & {sugar}",
        "Remove from heat",
      ],
    },
    {
      title: 'Blend marinade',
      bullets: [
        "Scrape saucepan contents into blender",
        "Add {garlic} & chiles plus their soaking liquid",
        "Blend on high until completely smooth, ~1 min, scraping sides as needed"
      ],
    },
    {
      title: 'Slice pork',
      bullets: [
        "Freeze {pork butt} 15 min to firm",
        "Slice as thin as possible",
        "Optionally, pound slices until less than {=fixed 1/4 in} thick",
      ],
    },
    {
      title: 'Marinate',
      bullets: [
        "Divide pork slices between sous vide bags",
        "Divide marinade between bags & work in until every slice is coated",
        "Seal in water bath to remove all air",
        "Refrigerate 4h (up to 36h)",
      ],
    },
    {
      title: 'Sous vide',
      bullets: [
        "Cook at {!150F} for 16h (up to 24h)",
      ],
    },
    {
      title: 'Chop',
      bullets: [
        "Remove bags from bath",
        "Transfer meat to cutting board, reserving cooking liquid",
        "Chop meat into taco-sized bits",
      ],
    },
    {
      title: 'Crisp',
      bullets: [
        "Heat {vegetable oil:3 tsp} in {vessel} over medium-high",
        "Add meat in even layer & cook undisturbed 5+ min, until a dark crust forms",
        "Scrape, mix & repeat 1–2 more times, until crisped & blackened in spots",
        "Reduce reserved cooking liquid in {vessel:pot} until consistency of thin paint",
        "Transfer meat to serving bowl",
        "Pour reserved cooking liquid over meat",
      ],
    },
    {
      title: 'Broil pineapple',
      bullets: [
        "Do while meat crisps",
        "Set {pineapple} on foil lined baking sheet & lightly oiled with {vegetable oil:1 tsp}",
        "Broil all sides until exterior is mostly black",
        "Chop finely",
      ],
    },
    {
      title: 'Serve',
      bullets: [
        "Pack meat & pineapple into double-stacked {corn tortillas}",
        "Serve with {white onion}, {fresh cilantro}, {salsa verde} & {limes}",
      ],
    },
  ],
};

export default recipe;
